import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from '../../../loaders';
import { FiltersConverter, SortsConverter } from '../../../../helpers';
import api from "../../../../services/api";
import { Col, Row } from 'reactstrap';
import { TooltipButton } from '../../../inputs';
import { AgGridReact } from 'ag-grid-react';
import qs from "qs";
import { ErrorNotification } from '../../../alerts';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import { IndeterminateCheckbox } from '../../../react-table/controls';

const pageSize = 50;
const apiUrl = "termination";

export default function TerminationApprovalList() {
    const [loading, setLoading] = useState(false);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const [isHR, setIsHR] = useState(false);
    const [showActiveApproval, setShowActiveApproval] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const gridRef = useRef();

    const loadingOverlayComponent = useMemo(() => Loader, []);

    useEffect(() => {
        api.get(`userprofile/group?username=${user.username}&group=HR`)
            .then(response => {
                setIsHR(response?.data?.userProfileId ? true : false);
            })
            .catch(error => console.error({error}))
        ;
    }, [user]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        editable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200,
            excelMode: 'mac',
        },
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true,
        },
        suppressMenu: true,
    }), []);

    const getServerSideDatasource = useCallback(() => {
        return {
            getRows: (params) => {
                const { request, requestId, action } = params;
                const { filterModel, sortModel } = request;

                const currentFilters = FiltersConverter(filterModel);
                const currentSorts = SortsConverter(sortModel);

                const queryString = qs.stringify({ filters: currentFilters, sortBy: currentSorts, showActiveApproval, showAll }, { allowDots: true });
                const url = `${apiUrl}/approval/${request.startRow ?? 0}/${pageSize}${requestId ? ("/" + requestId + "/" + action) : ""}${queryString ? `?${queryString}` : ""}`;

                api.get(url)
                    .then((response) => {
                        // console.log({response});
                        const successData = {
                            rowData: response.data.data,
                            rowCount: response.data.total,
                        };

                        params.success(successData);
                    })
                    .catch(error => {
                        params.fail();
                        console.error({error});
                    });
            },
        };
    }, [pageSize, showActiveApproval, showAll]);

    const onGridReady = useCallback((params) => {
        params.api.setServerSideDatasource(getServerSideDatasource());
        params.api.sizeColumnsToFit();
        window.addEventListener('resize', function () {
            setTimeout(function () {
                params.api.sizeColumnsToFit();
            });
        });
    }, [getServerSideDatasource]);

    const downloadDocument = useCallback((id, docType) => {
        setLoading(true);

        api.get(`${apiUrl}/attachment/${id}?docType=${docType}`, { responseType: 'blob' })
            .then(blob => {
                const filename = /(?<=attachment;.*filename=").*(?=";)/.exec(blob.headers["content-disposition"])[0]
                fileDownload(blob.data, filename);
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }, []);

    useEffect(() => {
        if (!gridRef?.current?.api)
            return;
        
        const datasource = getServerSideDatasource();
        gridRef.current.api.setServerSideDatasource(datasource);
    }, [showAll, showActiveApproval]);

    const columnDefs = [
        {
            field: "terminationNumber",
            headerName: "Exit Form Number",
            sort: "asc",
            minWidth: 100,
        },
        {
            field: "candidateName",
            headerName: "Employee",
            colId: "candidate.candidateName",
            minWidth: 150,
        },
        {
            field: "companyName",
            headerName: "Entity",
            minWidth: 180,
        },
        {
            field: "positionName",
            headerName: "Position",
            minWidth: 130,
        },
        {
            field: "projectName",
            headerName: "Project",
            minWidth: 150,
        },
        {
            field: "employmentTypeName",
            headerName: "Employment Type",
            minWidth: 120,
        },
        {
            field: "managerName",
            headerName: "Manager",
            minWidth: 150,
        },
        {
            field: "terminationReason",
            headerName: "Reason",
            minWidth: 100,
        },
        {
            field: "status.lookupLabel",
            headerName: "Status",
            minWidth: 100,
        },
        {
            field: "actions",
            headerName: "",
            filter: false,
            maxWidth: 90,
            minWidth: 90,
            pinned: "right",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const canEdit = rowData.status.lookupValue === "Initial";
                const {documentGenerated} = rowData;
                
                return (
                    <div key={`exit_action_${rowData.terminationApprovalId}`} id={`exit_action_${rowData.terminationApprovalId}`} className="text-center">
                        <TooltipButton
                            id={`exit_generate_approval_${rowData.terminationId}`}
                            title={`Approval${canEdit || isHR ? "" : " (View)"}`}
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => window.open(`/exitformapproval/${rowData.terminationId}/${rowData.terminationApprovalId}?isApproval=${true}`, '_blank')}
                        >
                            <i className="fa fa-xs fa-step-forward pt-1"></i>
                        </TooltipButton>
                        <TooltipButton
                            id={`exit_download_docx_${rowData.terminationId}`}
                            title="Download Document"
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => downloadDocument(rowData.terminationId)}
                            disabled={!documentGenerated}
                        >
                            <i className="fa fa-xs fa-file-word pt-1"></i>
                        </TooltipButton>
                    </div>
                );
            }
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <Row>
                <Col sm={12} className="d-flex flex-wrap justify-content-end align-items-center">
                    <IndeterminateCheckbox
                        id="showActiveApproval"
                        type="checkbox"
                        labelClassName="job-order-control mr-3"
                        onChange={() => {
                            const newValue = !showActiveApproval;
                            setShowActiveApproval(newValue);

                            if (!newValue && showAll)
                                setShowAll(newValue);
                        }}
                        checked={showActiveApproval}
                        customTitle="Show All Active Approval"
                    />
                    <IndeterminateCheckbox
                        id="showAll"
                        type="checkbox"
                        labelClassName="job-order-control mr-3"
                        onChange={() => {
                            const newValue = !showAll;
                            setShowAll(newValue);

                            if (newValue && !showActiveApproval)
                                setShowActiveApproval(newValue);
                        }}
                        checked={showAll}
                        customTitle="Show All Approval History"
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <div className="ag-theme-alpine ag-grid" style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            loadingOverlayComponent={loadingOverlayComponent}
                            suppressColumnVirtualisation={false}
                            suppressRowVirtualisation={false}
                            debounceVerticalScrollbar={false}
                            rowBuffer={20}
                            headerHeight={30}
                            groupHeaderHeight={30}
                            rowHeight={30}
                            floatingFiltersHeight={30}
                            suppressDragLeaveHidesColumns={true}
                            suppressColumnMoveAnimation={true}
                            animateRows={false}
                            pagination={true}
                            paginationPageSize={pageSize}
                            cacheBlockSize={pageSize}
                            rowModelType={'serverSide'}
                            serverSideInfiniteScroll={true}
                            onGridReady={onGridReady}
                            rowClass={'ag-row-normal'}
                        />
                    </div>
                </Col>
            </Row>
        {
            popupMessageObj?.type === "error" ?
            <ErrorNotification
                onConfirm={() => setPopupMessageObj(null)}
                message={popupMessageObj.message}
            />
            :
            null
        }
        </>
    );
};